import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  HELIX_HEAT_X_DEFAULTS,
} from '@/config/implicitPrimitives';

import {
  pickerContentClass,
  pickerHeroItemBaseClass,
  pickerHeroWrapperClass,
  pickerItemActiveClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { HEAT_EXCHANGERS, SubTab } from './data';

interface HeatExchangerDropdownProps {
  tab: SubTab;
  vehicleSubTab: string;
  implicitPrimitive: ImplicitPrimitive;
  setVehicleSubTab: (subTab: string) => void;
  setImplicitPrimitive: (p: ImplicitPrimitive) => void;
  setImplicitPrimitiveParams: (p: ImplicitPrimitiveParams) => void;
}

export function HeatExchangerDropdown({
  tab,
  vehicleSubTab,
  implicitPrimitive,
  setVehicleSubTab,
  setImplicitPrimitive,
  setImplicitPrimitiveParams,
}: HeatExchangerDropdownProps) {
  const isActive = vehicleSubTab === tab.value;
  const activeHx = HEAT_EXCHANGERS.find((h) => h.value === implicitPrimitive);
  // Always show a hero on sight — active selection's, else the first.
  const triggerHero =
    (isActive && activeHx ? activeHx.hero : undefined) ??
    HEAT_EXCHANGERS[0]?.hero;
  const handlePick = (h: (typeof HEAT_EXCHANGERS)[number]) => {
    setVehicleSubTab('heat-exchanger');
    setImplicitPrimitive(h.value);
    if (h.value === 'helix-heat-x') {
      setImplicitPrimitiveParams({
        kind: 'helix-heat-x',
        params: HELIX_HEAT_X_DEFAULTS,
      });
    }
  };
  return (
    <DropdownMenu key={tab.value}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            pillWidthClass,
            'flex items-center justify-center gap-2 self-stretch overflow-hidden rounded-md border px-4 text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          <span className="flex items-center gap-1 leading-tight">
            {tab.label}
            <ChevronDown className="h-3 w-3" />
          </span>
          {triggerHero && (
            <div className="flex w-14 flex-shrink-0 items-center justify-center self-stretch">
              <img
                src={triggerHero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={pickerContentClass}
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
      >
        {HEAT_EXCHANGERS.map((h) => (
          <DropdownMenuItem
            key={h.value}
            onClick={() => handlePick(h)}
            className={cn(
              pickerHeroItemBaseClass,
              implicitPrimitive === h.value && isActive
                ? pickerItemActiveClass
                : pickerItemInactiveClass,
            )}
          >
            <span className="font-medium">{h.label}</span>
            <div className={pickerHeroWrapperClass}>
              <img
                src={h.hero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
